import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { instructorsApi } from '../services/api';
import { Instructor, Move, Pattern, UpdateInstructorProfileData } from '../types';

const InstructorsManagement: React.FC = () => {
  const [showForm, setShowForm] = useState(false);
  const [editingInstructor, setEditingInstructor] = useState<Instructor | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [migratingInstructor, setMigratingInstructor] = useState<Instructor | null>(null);
  const [targetInstructorId, setTargetInstructorId] = useState<number | ''>('');
  const [selectedMoveIds, setSelectedMoveIds] = useState<Set<number>>(new Set());
  const [selectedPatternIds, setSelectedPatternIds] = useState<Set<number>>(new Set());
  const [migrateError, setMigrateError] = useState<string>('');
  const queryClient = useQueryClient();

  const { data: instructorsData, isLoading } = useQuery({
    queryKey: ['instructors', { page, search, statusFilter }],
    queryFn: () =>
      instructorsApi.getAll({
        page,
        limit: 10,
        search: search || undefined,
        status: statusFilter || undefined,
      }),
  });

  const { data: migrateSourceDetail } = useQuery({
    queryKey: ['instructor-detail', migratingInstructor?.id],
    queryFn: () => instructorsApi.getById(migratingInstructor!.id),
    enabled: !!migratingInstructor,
  });

  const { register, handleSubmit, reset } = useForm<UpdateInstructorProfileData>();

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateInstructorProfileData }) =>
      instructorsApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['instructors'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
      reset();
      setEditingInstructor(null);
      setShowForm(false);
    },
  });

  const activateMutation = useMutation({
    mutationFn: (id: number) => instructorsApi.update(id, { isActive: true }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['instructors'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
    },
  });

  const deactivateMutation = useMutation({
    mutationFn: (id: number) => instructorsApi.update(id, { isActive: false }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['instructors'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: instructorsApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['instructors'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
    },
    onError: (error: any) => {
      const message = error?.response?.data?.error || 'Failed to delete instructor';
      alert(message);
    },
  });

  const [isMigrating, setIsMigrating] = useState(false);

  const onSubmit = (data: UpdateInstructorProfileData) => {
    if (editingInstructor) {
      updateMutation.mutate({ id: editingInstructor.id, data });
    }
  };

  const handleEdit = (instructor: Instructor) => {
    setEditingInstructor(instructor);
    reset({
      bio: instructor.bio || '',
      profileImage: instructor.profileImage || '',
      isActive: instructor.isActive,
    });
    setShowForm(true);
  };

  const handleDelete = (instructor: Instructor) => {
    if (window.confirm(`Are you sure you want to delete "${instructor.name}"?`)) {
      deleteMutation.mutate(instructor.id);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingInstructor(null);
    reset();
  };

  const openMigrateModal = (instructor: Instructor) => {
    setMigratingInstructor(instructor);
    setTargetInstructorId('');
    setSelectedMoveIds(new Set());
    setSelectedPatternIds(new Set());
    setMigrateError('');
  };

  const closeMigrateModal = () => {
    setMigratingInstructor(null);
    setTargetInstructorId('');
    setSelectedMoveIds(new Set());
    setSelectedPatternIds(new Set());
    setMigrateError('');
    setIsMigrating(false);
  };

  const handleMigrate = async () => {
    if (!migratingInstructor || !targetInstructorId) return;

    const moves = (migrateSourceDetail as any)?.moves as Move[] | undefined;
    const patterns = (migrateSourceDetail as any)?.patterns as Pattern[] | undefined;
    const allMoveIds = moves?.map((m: Move) => m.id) || [];
    const allPatternIds = patterns?.map((p: Pattern) => p.id) || [];
    const migratingAllMoves = selectedMoveIds.size === 0 || selectedMoveIds.size === allMoveIds.length;
    const migratingAllPatterns = selectedPatternIds.size === 0 || selectedPatternIds.size === allPatternIds.length;

    // "All" defaults to migrating everything that exists. If the user explicitly
    // unchecked everything in a section that has items, treat that as "skip this kind."
    const skipMoves = allMoveIds.length > 0 && selectedMoveIds.size === 0 && selectedPatternIds.size > 0;
    const skipPatterns = allPatternIds.length > 0 && selectedPatternIds.size === 0 && selectedMoveIds.size > 0;

    setMigrateError('');
    setIsMigrating(true);
    try {
      let movedMoves = 0;
      let movedPatterns = 0;

      if (allMoveIds.length > 0 && !skipMoves) {
        const data: { targetInstructorId: number; moveIds?: number[] } = {
          targetInstructorId: Number(targetInstructorId),
        };
        if (!migratingAllMoves) data.moveIds = Array.from(selectedMoveIds);
        const r = await instructorsApi.migrateMoves(migratingInstructor.id, data);
        movedMoves = r.migratedCount;
      }

      if (allPatternIds.length > 0 && !skipPatterns) {
        const data: { targetInstructorId: number; patternIds?: number[] } = {
          targetInstructorId: Number(targetInstructorId),
        };
        if (!migratingAllPatterns) data.patternIds = Array.from(selectedPatternIds);
        const r = await instructorsApi.migratePatterns(migratingInstructor.id, data);
        movedPatterns = r.migratedCount;
      }

      queryClient.invalidateQueries({ queryKey: ['instructors'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
      alert(`Migrated ${movedMoves} move(s) and ${movedPatterns} pattern(s).`);
      closeMigrateModal();
    } catch (error: any) {
      setMigrateError(error?.response?.data?.error || 'Failed to migrate');
      setIsMigrating(false);
    }
  };

  const toggleMoveSelection = (moveId: number) => {
    setSelectedMoveIds((prev) => {
      const next = new Set(prev);
      if (next.has(moveId)) {
        next.delete(moveId);
      } else {
        next.add(moveId);
      }
      return next;
    });
  };

  const togglePatternSelection = (patternId: number) => {
    setSelectedPatternIds((prev) => {
      const next = new Set(prev);
      if (next.has(patternId)) {
        next.delete(patternId);
      } else {
        next.add(patternId);
      }
      return next;
    });
  };

  const toggleAllMoves = (moves: Move[]) => {
    const allIds = moves.map((m) => m.id);
    if (selectedMoveIds.size === allIds.length) {
      setSelectedMoveIds(new Set());
    } else {
      setSelectedMoveIds(new Set(allIds));
    }
  };

  const toggleAllPatterns = (patterns: Pattern[]) => {
    const allIds = patterns.map((p) => p.id);
    if (selectedPatternIds.size === allIds.length) {
      setSelectedPatternIds(new Set());
    } else {
      setSelectedPatternIds(new Set(allIds));
    }
  };

  const otherInstructors = instructorsData?.data.filter(
    (i) => i.id !== migratingInstructor?.id
  ) || [];

  const sourceMoves = ((migrateSourceDetail as any)?.moves as Move[] | undefined) || [];
  const sourcePatterns = ((migrateSourceDetail as any)?.patterns as Pattern[] | undefined) || [];

  return (
    <div className="instructors-management">
      <div className="page-header">
        <h1>Instructors Management</h1>
      </div>

      {showForm && editingInstructor && (
        <div className="form-container">
          <h2>Edit Instructor Profile</h2>
          <form onSubmit={handleSubmit(onSubmit)} className="admin-form">
            <div className="form-row">
              <div className="form-group">
                <label>Name</label>
                <input
                  type="text"
                  value={editingInstructor.name}
                  disabled
                  className="disabled"
                />
              </div>

              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  value={editingInstructor.email}
                  disabled
                  className="disabled"
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="bio">Bio</label>
              <textarea
                {...register('bio')}
                id="bio"
                rows={4}
                placeholder="Brief description of the instructor's background and expertise..."
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="profileImage">Profile Image URL</label>
                <input
                  {...register('profileImage')}
                  type="url"
                  id="profileImage"
                  placeholder="https://example.com/profile.jpg"
                />
              </div>

              <div className="form-group" />
            </div>

            <div className="form-actions">
              <button type="submit" className="btn btn-primary">
                Update Instructor
              </button>
              <button type="button" className="btn btn-secondary" onClick={handleCancel}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {migratingInstructor && (
        <div className="modal-overlay" onClick={closeMigrateModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>Migrate Content</h2>
            <p>
              Migrate moves and/or patterns from <strong>{migratingInstructor.name}</strong> to another instructor.
            </p>

            <div className="form-group">
              <label htmlFor="targetInstructor">Target Instructor</label>
              <select
                id="targetInstructor"
                value={targetInstructorId}
                onChange={(e) => setTargetInstructorId(e.target.value ? Number(e.target.value) : '')}
              >
                <option value="">Select an instructor...</option>
                {otherInstructors.map((i) => (
                  <option key={i.id} value={i.id}>
                    {i.name}
                  </option>
                ))}
              </select>
            </div>

            {sourceMoves.length > 0 && (
              <div className="form-group">
                <label>
                  Select Moves to Migrate
                  <button
                    type="button"
                    className="btn btn-small btn-secondary migrate-select-all-btn"
                    onClick={() => toggleAllMoves(sourceMoves)}
                  >
                    {selectedMoveIds.size === sourceMoves.length ? 'Deselect All' : 'Select All'}
                  </button>
                </label>
                <div className="migrate-moves-list">
                  {sourceMoves.map((move: Move) => (
                    <label key={move.id} className="checkbox-label migrate-item-label">
                      <input
                        type="checkbox"
                        checked={selectedMoveIds.has(move.id)}
                        onChange={() => toggleMoveSelection(move.id)}
                      />
                      <span>{move.name || move.title}</span>
                      <span className={`status migrate-item-status ${move.isPublished ? 'active' : 'inactive'}`}>
                        {move.isPublished ? 'Published' : 'Draft'}
                      </span>
                    </label>
                  ))}
                </div>
                <small>
                  {selectedMoveIds.size === 0
                    ? `All ${sourceMoves.length} moves will be migrated`
                    : `${selectedMoveIds.size} of ${sourceMoves.length} moves selected`}
                </small>
              </div>
            )}

            {sourcePatterns.length > 0 && (
              <div className="form-group">
                <label>
                  Select Patterns to Migrate
                  <button
                    type="button"
                    className="btn btn-small btn-secondary migrate-select-all-btn"
                    onClick={() => toggleAllPatterns(sourcePatterns)}
                  >
                    {selectedPatternIds.size === sourcePatterns.length ? 'Deselect All' : 'Select All'}
                  </button>
                </label>
                <div className="migrate-patterns-list">
                  {sourcePatterns.map((pattern: Pattern) => (
                    <label key={pattern.id} className="checkbox-label migrate-item-label">
                      <input
                        type="checkbox"
                        checked={selectedPatternIds.has(pattern.id)}
                        onChange={() => togglePatternSelection(pattern.id)}
                      />
                      <span>{pattern.title}</span>
                      <span className={`status migrate-item-status ${pattern.isPublished ? 'active' : 'inactive'}`}>
                        {pattern.isPublished ? 'Published' : 'Draft'}
                      </span>
                    </label>
                  ))}
                </div>
                <small>
                  {selectedPatternIds.size === 0
                    ? `All ${sourcePatterns.length} patterns will be migrated`
                    : `${selectedPatternIds.size} of ${sourcePatterns.length} patterns selected`}
                </small>
              </div>
            )}

            {sourceMoves.length === 0 && sourcePatterns.length === 0 && (
              <p>This instructor has no moves or patterns to migrate.</p>
            )}

            {migrateError && <div className="error-message">{migrateError}</div>}

            <div className="form-actions">
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleMigrate}
                disabled={!targetInstructorId || isMigrating || (sourceMoves.length === 0 && sourcePatterns.length === 0)}
              >
                {isMigrating ? 'Migrating...' : 'Migrate'}
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={closeMigrateModal}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="table-controls">
        <input
          type="text"
          placeholder="Search instructors..."
          aria-label="Search instructors"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="search-input"
        />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="status-filter"
          aria-label="Filter by status"
        >
          <option value="">All Status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
      </div>

      {isLoading ? (
        <div className="loading">Loading instructors...</div>
      ) : instructorsData && instructorsData.data.length > 0 ? (
        <>
          <div className="table-container">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Status</th>
                  <th>Content</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {instructorsData.data.map((instructor) => (
                  <tr key={instructor.id}>
                    <td>
                      <div className="instructor-info">
                        <strong>{instructor.name}</strong>
                        {instructor.bio && (
                          <div className="instructor-bio">
                            {instructor.bio.length > 100
                              ? `${instructor.bio.substring(0, 100)}...`
                              : instructor.bio}
                          </div>
                        )}
                      </div>
                    </td>
                    <td>{instructor.email}</td>
                    <td>
                      <span className={`status ${instructor.isActive ? 'active' : 'inactive'}`}>
                        {instructor.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      <div className="moves-stats">
                        <span>{instructor.publishedMoves || 0} / {instructor.totalMoves || 0} moves</span>
                        <small>{instructor.publishedPatterns || 0} / {instructor.totalPatterns || 0} patterns</small>
                      </div>
                    </td>
                    <td>{new Date(instructor.createdAt).toLocaleDateString()}</td>
                    <td>
                      <div className="action-buttons">
                        {instructor.isActive ? (
                          <button
                            className="btn btn-small btn-warning"
                            onClick={() => {
                              if (window.confirm(`Deactivating "${instructor.name}" will revoke their Cognito access. Continue?`)) {
                                deactivateMutation.mutate(instructor.id);
                              }
                            }}
                            disabled={deactivateMutation.isPending}
                          >
                            Deactivate
                          </button>
                        ) : (
                          <button
                            className="btn btn-small btn-success"
                            onClick={() => activateMutation.mutate(instructor.id)}
                            disabled={activateMutation.isPending}
                          >
                            Activate
                          </button>
                        )}
                        {((instructor.totalMoves || 0) + (instructor.totalPatterns || 0)) > 0 && (
                          <button
                            className="btn btn-small btn-secondary"
                            onClick={() => openMigrateModal(instructor)}
                          >
                            Migrate
                          </button>
                        )}
                        <button
                          className="btn btn-small btn-secondary"
                          onClick={() => handleEdit(instructor)}
                        >
                          Edit
                        </button>
                        <button
                          className="btn btn-small btn-danger"
                          onClick={() => handleDelete(instructor)}
                          disabled={deleteMutation.isPending}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {instructorsData.pagination.pages > 1 && (
            <div className="pagination">
              {Array.from({ length: instructorsData.pagination.pages }, (_, i) => i + 1).map(
                (pageNum) => (
                  <button
                    key={pageNum}
                    className={`btn btn-small ${
                      pageNum === page ? 'btn-primary' : 'btn-secondary'
                    }`}
                    onClick={() => setPage(pageNum)}
                  >
                    {pageNum}
                  </button>
                )
              )}
            </div>
          )}
        </>
      ) : (
        <div className="empty-state">
          <p>No instructors found.</p>
        </div>
      )}
    </div>
  );
};

export default InstructorsManagement;
